/**
 * Composant de formulaire de connexion
 * Permet aux utilisateurs de s'authentifier avec un pseudo et un mot de passe
 */
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import '../styles/auth.css';


/**
 * LoginForm - Composant de formulaire d'authentification
 * @returns {JSX.Element} Formulaire de connexion avec gestion d'erreurs
 */
export default function LoginForm() {
    // Hook d'authentification fournissant les fonctions de login et les états
    const { login, loading, error } = useAuth();
    // Hook de navigation pour rediriger après connexion réussie
    const navigate = useNavigate();
    // État local pour le pseudo de l'utilisateur
    const [pseudo, setPseudo] = useState('');
    // État local pour le mot de passe de l'utilisateur
    const [password, setPassword] = useState('');
    // État local pour mémoriser la connexion
    const [rememberMe, setRememberMe] = useState(true);

    /**
     * Gère la soumission du formulaire de connexion
     * @param {Event} e - L'événement de soumission du formulaire
     */
    const handleSubmit = async (e) => {
        // Empêche le rechargement de la page lors de la soumission
        e.preventDefault();
        try {
            // Tente de connecter l'utilisateur avec les identifiants fournis
            await login(pseudo, password, rememberMe);
            // Redirige vers la page main en cas de succès
            navigate('/main');
        } catch (err) {
            // Affiche l'erreur dans la console (l'erreur visuelle est gérée par le hook useAuth)
            console.error('Erreur de connexion:', err);
        }
    };

    return (
        <div className="login-container">
            <div className="login-form-wrapper">
                {/* Logo */}
                <div className="logo">
                    <img src="/logo.png" alt="Culture Quiz Logo" />
                </div>

                {/* Titre */}
                <h1 className="login-title">SE CONNECTER</h1>

                {/* Description */}
                <p className="login-description">
                    Vous devez être connecté pour jouer aux quiz. <br />
                    Votre compte permet de sauvegarder votre progression et de jouer sur plusieurs appareils.
                </p>

                {/* Formulaire de connexion */}
                <form className="login-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <input
                            className="form-input"
                            type="text"
                            placeholder="Pseudo"
                            value={pseudo}
                            onChange={(e) => setPseudo(e.target.value)}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <input
                            className="form-input"
                            type="password"
                            placeholder="Mot de passe"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    <label className="remember-row">
                        <input
                            className="remember-checkbox"
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        <span className="remember-label">Mémoriser ma connexion</span>
                    </label>

                    <button type="submit" className="submit-button" disabled={loading}>
                        {loading ? 'Connexion...' : 'Valider'}
                    </button>

                    {error && <p className="error-message">{error}</p>}
                </form>

                {/* Liens d'authentification */}
                <div className="auth-links">
                    <div className="auth-link-group">
                        <h3>Je n'ai pas encore de compte ?</h3>
                        <p>Vous pouvez créer votre compte en cliquant <a href="/signup" className="auth-link">ICI</a>.</p>
                    </div>

                    <div className="auth-link-group">
                        <h3>Mots de passe oublié ?</h3>
                        <p>Nous sommes désolé. Si vous souhaitez réinitialiser votre mots de passe merci de nous écrire par mail <a href="mailto:contact@quiz-app.fr" className="auth-link">contact@quiz-app.fr</a></p>
                    </div>
                </div>

                {/* Footer avec liens légaux */}
                <div className="footer-links">
                    <a href="/privacy" className="footer-link">Politique de confidentialité</a>
                    <a href="/terms" className="footer-link">CGU</a>
                </div>
            </div>
        </div>
    );
}
